import { Body, Icon, Status } from '@assembly-js/design-system';
import { Menu, MenuItem } from '@mui/material';
import { t } from 'i18next';
import React, { useState } from 'react';

interface CreditTierSelectProps {
  /** Selectable monthly credit totals (included allowance + ladder steps). */
  options: number[];
  /** The subscription's current total, badged "Current" in the list. */
  currentCredits?: number;
  /**
   * When set, options read as extras above this baseline ("+100 additional
   * monthly credits") instead of totals. The add-credits modal passes the
   * subscribed total and only offers rungs above it, so every label is a
   * purchase delta; the upgrade modal passes the target plan's included
   * allowance, including a "no additional" option at that allowance.
   */
  additionalFromCredits?: number;
  /** Replaces the default ladder labels, e.g. "+100 Pro credits". */
  formatOption?: (credits: number) => string;
  selectedCredits: number;
  onSelectCredits: (credits: number) => void;
}

export const CreditTierSelect: React.FC<CreditTierSelectProps> = ({
  options,
  currentCredits,
  additionalFromCredits,
  formatOption,
  selectedCredits,
  onSelectCredits,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const getOptionLabel = (credits: number): string => {
    if (formatOption) {
      return formatOption(credits);
    }
    if (additionalFromCredits === undefined) {
      return t(($) => $.page_studio_plans.choose_plan.credits_option, {
        count: credits,
      });
    }
    const additional = credits - additionalFromCredits;
    return additional > 0
      ? t(($) => $.page_studio_plans.choose_plan.credits_option_additional, {
          count: additional,
        })
      : t(($) => $.page_studio_plans.choose_plan.credits_option_no_additional);
  };

  return (
    <>
      <button
        type="button"
        className="box-border flex h-7 w-full cursor-pointer items-center justify-between gap-1 rounded border border-solid border-divider-card bg-white px-2 truncate"
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {/* Truncates rather than wraps: the card column narrows with the
            viewport and a second line spilled out through the 28px border. */}
        <Body
          size="sm"
          tag="span"
          className="min-w-0 truncate leading-[21px] text-black-heading"
        >
          {getOptionLabel(selectedCredits)}
        </Body>
        <Icon
          icon="ChevronDown"
          className="h-3 w-3 shrink-0 text-black-heading"
        />
      </button>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{ className: 'min-w-[240px]' }}
      >
        {options.map((credits) => (
          <MenuItem
            key={credits}
            selected={credits === selectedCredits}
            onClick={() => {
              onSelectCredits(credits);
              setAnchorEl(null);
            }}
          >
            <Body size="sm" tag="span">
              {formatOption !== undefined || additionalFromCredits !== undefined
                ? getOptionLabel(credits)
                : t(
                    ($) => $.page_studio_plans.choose_plan.credits_option_short,
                    { count: credits },
                  )}
            </Body>
            {/* The subscribed total is badged and the chosen one checked, as
                separate marks: a card usually opens on the subscribed total,
                but a pick that moves off it still has to read as chosen. */}
            <span className="ml-auto flex shrink-0 items-center gap-2 pl-3">
              {credits === currentCredits && (
                <Status
                  label={t(
                    ($) => $.page_studio_plans.choose_plan.credits_current,
                  )}
                  status="info"
                />
              )}
              {credits === selectedCredits && (
                <Icon icon="Check" className="h-4 w-4 text-black-heading" />
              )}
            </span>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
